import { z } from 'zod';
import { placeholderFortuneImage } from '@/appconstants';

const Authors = z.array(
  z.object({
    name: z.string().catch('Fortune Editors'),
    url: z.string().catch('/author/fortune-editors'),
  }),
);

export const ArticlePreview = z.object({
  authors: Authors.catch([{ name: 'Fortune Editors', url: '/author/fortune-editors' }]),
  classification: z
    .object({
      primaryPostTag: z.object({
        databaseId: z.number().catch(-1),
        name: z.string().catch(''),
        url: z.string().catch('/'),
      }),
    })
    .catch({
      primaryPostTag: {
        databaseId: -1,
        name: '',
        url: '/',
      },
    }),
  databaseId: z.number().catch(-1),
  dateGmt: z.string().transform((rawString) => {
    if (!Number(Date.parse(rawString))) {
      return new Date().toISOString();
    }
    // Necessary as some values are local when they should be in UTC
    if (!rawString.endsWith('Z')) {
      return `${rawString}Z`;
    }
    return rawString;
  }),
  excerpt: z.string().catch('placeholder excerpt'),
  // NOTE: Need to figure out on the backend how to ensure all articles have an id
  id: z.string().catch('placeholder id'),
  image: z.string().catch(placeholderFortuneImage),
  // NOTE: Should probably update the backend to require an image alt
  imageAlt: z.string().catch('placeholder alt text'),
  primarySectionId: z
    .string()
    .transform((rawString) => {
      const cleanedString = rawString.replace(/^"|"$/g, '');
      const num = Number(cleanedString);

      return Number.isNaN(num) ? -1 : num;
    })
    .catch(-1),
  sections: z
    .object({
      nodes: z
        .array(
          z.object({
            databaseId: z.number().catch(-1),
            name: z.string().catch('Eyebrow'),
            url: z.string().catch('/'),
          }),
        )
        .min(1)
        .catch([{ databaseId: -1, name: 'Eyebrow', url: '/' }]),
    })
    .catch({ nodes: [{ databaseId: -1, name: 'Eyebrow', url: '/' }] }),
  slug: z.string().catch('/'),
  title: z.string().catch('Title'),
  url: z.string().catch('/'),
});

export const ArticlePreviewRailFull = ArticlePreview.omit({
  classification: true,
  databaseId: true,
  excerpt: true,
  primarySectionId: true,
  sections: true,
}).extend({
  sectionName: z.string().catch('Eyebrow'),
  sectionUrl: z.string(),
});

export const ArticlePreviewHero = ArticlePreview;

export const ArticlePreviewFeatured = ArticlePreview.omit({
  classification: true,
  primarySectionId: true,
  sections: true,
});

export const ArticlePreviewSmall = ArticlePreview.omit({
  classification: true,
  excerpt: true,
  primarySectionId: true,
  sections: true,
});

export const ArticlePreviewMostPopular = z.object({
  authors: Authors.catch([{ name: 'Fortune Editors', url: '/author/fortune-editors' }]),
  dateGmt: z
    .string()
    .transform((rawString) => {
      if (!Number(Date.parse(rawString))) {
        return new Date().toISOString();
      }
      // Necessary as some values are local when they should be in UTC
      if (!rawString.endsWith('Z')) {
        return `${rawString}Z`;
      }
      return rawString;
    })
    .catch(new Date().toDateString()),
  image: z.string().catch(placeholderFortuneImage),
  sectionName: z.string().catch(''),
  sectionUrl: z.string().catch('/'),
  title: z.string(),
  url: z.string().catch('/'),
});

export const ArticlePreviewGrid = ArticlePreview.omit({
  classification: true,
  excerpt: true,
});

export type ArticlePreview = z.infer<typeof ArticlePreview>;
export type ArticlePreviewRailFull = z.infer<typeof ArticlePreviewRailFull>;
export type ArticlePreviewHero = z.infer<typeof ArticlePreviewHero>;
export type ArticlePreviewSmall = z.infer<typeof ArticlePreviewSmall>;
export type ArticlePreviewFeatured = z.infer<typeof ArticlePreviewFeatured>;
export type ArticlePreviewMostPopular = z.infer<typeof ArticlePreviewMostPopular>;
export type ArticlePreviewGrid = z.infer<typeof ArticlePreviewGrid>;
